import React, { useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Divider,
  Flex,
  Stack,
  Text,
  Badge,
  Center,
  Button,
  useToast,
  // CardBody,
  // Card,
  // FormLabel,
  // Switch,
} from '@chakra-ui/react';
// Importa le icone che desideri (qui alcuni esempi)
import {
  FaEnvelope,
  FaUser,
  FaUsers,
  FaShoppingCart,
  FaStar,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CIL_POINTS_LABEL, formatCilPoints } from '../../utils/cilPoints';
import InfoCILPoints from '../CILPoints/InfoCILPoints';

const Tesseramento = ({ user, handleLogout, onUserDataUpdate }) => {
  const [isToggling, setIsToggling] = useState(false);
  const toast = useToast();

  const {
    email,
    orders = [],
    tesseramenti = [],
    firstName,
    lastName,
    punti,
  } = user;

  const saldoPunti = Number(punti) || 0;

  const handleToggleNewsletter = async () => {
    setIsToggling(true);
    try {
      const response = await axios.post(
        '/api/users/me/toggle-newsletter',
        {},
        {
          withCredentials: true,
        }
      );

      if (response.data) {
        toast({
          title: 'Newsletter aggiornata',
          description: response.data.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Aggiorna i dati dell'utente per riflettere le modifiche
        if (onUserDataUpdate) {
          // Piccolo delay per assicurarsi che Strapi abbia completato l'aggiornamento
          setTimeout(() => {
            onUserDataUpdate();
          }, 1000); // Aumentato il delay per dare più tempo a Strapi
        }
      }
    } catch (error) {
      console.error('Errore durante il toggle della newsletter:', error);

      // Gestione più specifica degli errori
      let errorMessage = "Errore durante l'aggiornamento della newsletter";

      if (error.response?.status === 500) {
        errorMessage =
          'Errore del server. La newsletter potrebbe essere stata aggiornata ma si è verificato un problema di comunicazione.';
        // Anche in caso di errore 500, proviamo ad aggiornare i dati dell'utente
        if (onUserDataUpdate) {
          setTimeout(() => {
            onUserDataUpdate();
          }, 1000);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast({
        title: 'Errore',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsToggling(false);
    }
  };

  const accontiDaSaldare = useMemo(() => {
    const summaries = user.eventPaymentSummaries || [];
    if (summaries.length > 0) {
      return summaries.filter(
        (summary) =>
          summary.daPagareOra > 0.01 ||
          summary.saldoFuturo > 0.01 ||
          summary.saldoDaPagare > 0.01 ||
          summary.residuoTotale > 0.01
      );
    }

    if (user.orders) {
      const ordersDaSaldare = orders.filter((order) =>
        order.metadata?.products?.some(
          ({ paymentType }) => paymentType === 'acconto'
        )
      );

      return ordersDaSaldare;
    }

    return [];
  }, [user, orders]);

  return (
    <Box>
      <Box
        bg="#2f3136"
        p={{ base: 6, md: 8 }}
        borderRadius="20px"
        boxShadow="0 20px 40px rgba(0,0,0,0.3)"
        border="1px solid"
        borderColor="gray.600"
      >
        <Stack spacing={4} align="center">
          <Heading
            as="h2"
            size="lg"
            textAlign="center"
            color="white"
            fontWeight="800"
          >
            Panoramica Account
          </Heading>
          <Text textAlign="center" fontSize="sm" color="gray.300">
            Queste sono le informazioni principali del tuo account.
          </Text>
          <Divider />

          {/* Griglia 2x2 con Flex, ma ogni campo è in una "card" */}
          <Flex wrap="wrap" justify="center" gap={6} mt={4}>
            {/* Email */}
            {email && (
              <Box
                flexBasis={{ base: '100%', md: '45%' }}
                bg="#36393f"
                p={4}
                borderRadius="6px"
                textAlign="center"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.600"
                _hover={{
                  borderColor: '#ffcc00',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                transition="all 0.2s ease"
              >
                <Text as="strong" fontSize="sm" color="gray.300">
                  <FaEnvelope
                    style={{
                      display: 'inline-block',
                      marginRight: 6,
                      color: '#ffcc00',
                    }}
                  />
                  Email
                </Text>
                <Text
                  data-testid="account-informazioni-email"
                  data-clarity-mask="True"
                  fontSize="lg"
                  color="white"
                >
                  {email}
                </Text>
              </Box>
            )}

            {/* Nome e Cognome */}
            {(firstName || lastName) && (
              <Box
                flexBasis={{ base: '100%', md: '45%' }}
                bg="#36393f"
                p={4}
                borderRadius="6px"
                textAlign="center"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.600"
                _hover={{
                  borderColor: '#ffcc00',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                transition="all 0.2s ease"
              >
                <Text as="strong" fontSize="sm" color="gray.300">
                  <FaUser
                    style={{
                      display: 'inline-block',
                      marginRight: 6,
                      color: '#ffcc00',
                    }}
                  />
                  Nome e cognome utente
                </Text>
                <Text
                  data-testid="informazioni-nome-cognome"
                  fontSize="lg"
                  color="white"
                  data-clarity-mask="True"
                >
                  {firstName} {lastName}
                </Text>
              </Box>
            )}

            {/* Tesserati */}
            {tesseramenti.length > 0 && (
              <Box
                flexBasis={{ base: '100%', md: '45%' }}
                bg="#36393f"
                p={4}
                borderRadius="6px"
                textAlign="center"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.600"
                _hover={{
                  borderColor: '#ffcc00',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                transition="all 0.2s ease"
              >
                <Text as="strong" fontSize="sm" color="gray.300">
                  <FaUsers
                    style={{
                      display: 'inline-block',
                      marginRight: 6,
                      color: '#ffcc00',
                    }}
                  />
                  Tesserati
                </Text>
                <Stack spacing={1} align="center" mt={2}>
                  {tesseramenti.map((tesseramento) => {
                    // Trova l'ordine collegato al tesseramento e al socio
                    const relatedOrder = orders.find(
                      (order) =>
                        order.tesseramento?.documentId ===
                          tesseramento.documentId ||
                        order.tesseramento?.id === tesseramento.documentId ||
                        (order.socio?.documentId === tesseramento.documentId &&
                          order.tesseramento)
                    );

                    // Determina il colore del badge in base allo stato dell'ordine
                    let badgeBg = '#36393f';
                    let badgeColor = '#ffcc00';
                    let badgeBorderColor = '#ffcc00';

                    if (relatedOrder) {
                      if (relatedOrder.paymentStatus === 'pending') {
                        badgeBg = '#ffcc00';
                        badgeColor = 'black';
                        badgeBorderColor = '#ffcc00';
                      } else if (
                        relatedOrder.paymentStatus === 'succeeded' ||
                        relatedOrder.paymentStatus === 'processing'
                      ) {
                        badgeBg = '#3eb297';
                        badgeColor = 'white';
                        badgeBorderColor = '#3eb297';
                      }
                    }

                    return (
                      <Badge
                        key={tesseramento.documentId}
                        bg={badgeBg}
                        color={badgeColor}
                        border="1px solid"
                        borderColor={badgeBorderColor}
                        px={2}
                        py={1}
                        borderRadius="4px"
                        fontSize="sm"
                        fontWeight="500"
                        data-clarity-mask="True"
                      >
                        {tesseramento.nomeSocio}
                      </Badge>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Newsletter Status */}
            <Box
              flexBasis={{ base: '100%', md: '45%' }}
              bg="#36393f"
              p={4}
              borderRadius="6px"
              textAlign="center"
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.600"
              _hover={{
                borderColor: '#ffcc00',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              transition="all 0.2s ease"
            >
              <Text as="strong" fontSize="sm" color="gray.300" mb={3}>
                <FaEnvelope
                  style={{
                    display: 'inline-block',
                    marginRight: 6,
                    color: '#ffcc00',
                  }}
                />
                Newsletter
              </Text>
              <Stack spacing={3} align="center">
                <Text fontSize="lg" color="white">
                  {tesseramenti.some((t) => !t.isMinorenne && t.sendNewsletter)
                    ? 'Attiva'
                    : 'Non attiva'}
                </Text>
                <Button
                  bg={
                    tesseramenti.some((t) => !t.isMinorenne && t.sendNewsletter)
                      ? '#e73341'
                      : '#3eb297'
                  }
                  color="white"
                  size="sm"
                  variant="outline"
                  onClick={handleToggleNewsletter}
                  isLoading={isToggling}
                  loadingText="Aggiornamento..."
                  disabled={isToggling}
                  borderRadius="6px"
                  fontWeight="600"
                  _hover={{
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  }}
                  transition="all 0.2s ease"
                >
                  {tesseramenti.some((t) => !t.isMinorenne && t.sendNewsletter)
                    ? 'Disattiva'
                    : 'Attiva'}
                </Button>
              </Stack>
            </Box>

            {/* Totale Ordini */}
            <Box
              flexBasis={{ base: '100%', md: '45%' }}
              bg="#36393f"
              p={4}
              borderRadius="6px"
              textAlign="center"
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.600"
              _hover={{
                borderColor: '#ffcc00',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              transition="all 0.2s ease"
            >
              <Text as="strong" fontSize="sm" color="gray.300">
                <FaShoppingCart
                  style={{
                    display: 'inline-block',
                    marginRight: 6,
                    color: '#ffcc00',
                  }}
                />
                Totale Ordini
              </Text>
              <Text fontSize="lg" color="white">
                {orders.length}
              </Text>
            </Box>

            {/* Saldo punti */}
            <Box
              flexBasis={{ base: '100%', md: '45%' }}
              bg="#36393f"
              p={4}
              borderRadius="6px"
              textAlign="center"
              boxShadow="sm"
              border="1px solid"
              borderColor="gray.600"
              _hover={{
                borderColor: '#ffcc00',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              transition="all 0.2s ease"
            >
              <Text as="strong" fontSize="sm" color="gray.300">
                <FaStar
                  style={{
                    display: 'inline-block',
                    marginRight: 6,
                    color: '#b794f4',
                  }}
                />
                Saldo {CIL_POINTS_LABEL}
              </Text>
              <Text
                fontSize="lg"
                color="white"
                data-testid="account-informazioni-punti"
              >
                {formatCilPoints(saldoPunti)}
              </Text>
              <Box mt={2}>
                <InfoCILPoints variant="link" tone="account" size="xs" />
              </Box>
            </Box>

            {/* Totale Ordini Da Saldare */}
            {Boolean(accontiDaSaldare.length) && (
              <Box
                flexBasis={{ base: '100%', md: '45%' }}
                bg="#36393f"
                p={4}
                borderRadius="6px"
                textAlign="center"
                boxShadow="sm"
                border="1px solid"
                borderColor="gray.600"
                _hover={{
                  borderColor: '#ffcc00',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                transition="all 0.2s ease"
              >
                <Text as="strong" fontSize="sm" color="gray.300">
                  <FaShoppingCart
                    style={{
                      display: 'inline-block',
                      marginRight: 6,
                      color: '#ffcc00',
                    }}
                  />
                  Acconti da saldare
                </Text>
                <Text fontSize="lg" color="white">
                  {accontiDaSaldare.length}
                </Text>
                <Button
                  as={Link}
                  to="/account/ordini"
                  bg="#ffcc00"
                  color="black"
                  size="sm"
                  mt={2}
                  borderRadius="6px"
                  fontWeight="600"
                  _hover={{
                    bg: '#ffdd44',
                    boxShadow: '0 4px 12px rgba(255, 204, 0, 0.3)',
                  }}
                  transition="all 0.2s ease"
                >
                  Visualizza tutti gli ordini
                </Button>
              </Box>
            )}
          </Flex>

          <Center display={{ base: 'flex', md: 'none' }} mt={6}>
            <Button
              onClick={handleLogout}
              bg="#e73341"
              color="white"
              size="sm"
              borderRadius="6px"
              fontWeight="600"
              data-testid="logout-button"
              _hover={{
                bg: '#d62d3a',
                boxShadow: '0 4px 12px rgba(231, 51, 65, 0.3)',
              }}
              transition="all 0.2s ease"
            >
              Esci dal sito
            </Button>
          </Center>
          {/* <Box p={4}>
            <Card maxW="md" mx="auto" boxShadow="md">
              <CardBody>
                <Text fontSize="xl" fontWeight="bold" mb={4}>
                  Iscriviti alla Newsletter
                </Text>

                <FormLabel
                  htmlFor="newsletter-switch"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text>
                    {isSubscribed ? 'Sei iscritto' : 'Non sei iscritto'}
                  </Text>
                  <Switch
                    id="newsletter-switch"
                    colorScheme="teal"
                    isChecked={isSubscribed}
                    onChange={handleToggleSubscription}
                  />
                </FormLabel>
              </CardBody>
            </Card>
          </Box> */}
        </Stack>
      </Box>
    </Box>
  );
};

export default Tesseramento;
